import React, { useState, useEffect } from 'react';
import './MasterPortal.scss';
import logo from '../../img/avionica.png';
import ProgressBar from '../Common/ProgressBar/ProgressBar';
import { downloadWithProgress } from '../../common/downloadWithProgress';

const STATUSES = ['Pending', 'Processing', 'Reviewed', 'Complete', 'Flagged'];
const API_URL = process.env.REACT_APP_API_URL;
// ── PasswordForm sub-component ────────────────────────────────────────────────
const PasswordForm = ({ username, token, onDone }) => {
  const [pw, setPw]           = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) return setError('Password must be at least 8 characters.');
    if (pw !== confirm) return setError('Passwords do not match.');
    setSaving(true);
    try {
      const res  = await fetch(`${API_URL}/master/users/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ targetUsername: username, newPassword: pw })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to update password.');
      setSuccess(true);
      setTimeout(onDone, 1200);
    } catch { setError('Request failed. Try again.'); }
    finally  { setSaving(false); }
  };

  if (success) return <p className="um-success">Password updated!</p>;
  return (
    <form className="um-pw-form" onSubmit={handleSubmit}>
      <input type="password" placeholder="New password (min 8 chars)" value={pw}      onChange={e => setPw(e.target.value)}      autoComplete="new-password" />
      <input type="password" placeholder="Confirm new password"       value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" />
      {error && <p className="um-error">{error}</p>}
      <div className="um-pw-actions">
        <button type="submit" className="um-btn save-btn"   disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" className="um-btn cancel-btn" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
};

// ── main component ────────────────────────────────────────────────────────────
const MasterPortal = () => {
  // ── auth
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginUsername, setLoginUsername]     = useState('');
  const [loginPassword, setLoginPassword]     = useState('');
  const [loginError, setLoginError]           = useState('');
  const [token, setToken]                     = useState(null);   // JWT — replaces credentials
  const [loggedInAs, setLoggedInAs]           = useState('');     // just for display

  // ── tabs
  const [activeTab, setActiveTab] = useState('files');

  // ── files tab
  const [files, setFiles]                   = useState([]);
  const [loadingFiles, setLoadingFiles]     = useState(false);
  const [collapsed, setCollapsed]           = useState({});
  const [statusUpdating, setStatusUpdating] = useState({});
  const [assignUpdating, setAssignUpdating] = useState({});
  const [deletingFile, setDeletingFile]     = useState(null);
  const [fileDeleting, setFileDeleting]     = useState({});
  const [downloadingFile, setDownloadingFile] = useState(null);  // filename currently downloading
  const [downloadPercent, setDownloadPercent] = useState(0);     // 0..100, or null when size unknown
  const [fetchError, setFetchError]         = useState('');
  const [globalSearch, setGlobalSearch]     = useState('');
  const [colFilters, setColFilters]         = useState({ file: '', serial: '', date: '', tail: '', status: '', assign: '' });
  const [sort, setSort]                     = useState({ key: '', dir: 'asc' });
  const [filtersVisible, setFiltersVisible] = useState(false);

  const [inlineNew, setInlineNew] = useState({ filename: null, name: '', email: '', saving: false, error: '' });

  // ── assignees
  const [assignees, setAssignees] = useState([]);

  // ── users tab
  const [users, setUsers]                   = useState([]);
  const [loadingUsers, setLoadingUsers]     = useState(false);
  const [usersError, setUsersError]         = useState('');
  const [expandedPw, setExpandedPw]         = useState(null);
  const [deletingUser, setDeletingUser]     = useState(null);
  const [addUserForm, setAddUserForm]       = useState({ open: false, username: '', password: '', confirm: '', isAdmin: false, isSupport: false, companies: '', email: '' });
  const [addUserError, setAddUserError]     = useState('');
  const [addUserSaving, setAddUserSaving]   = useState(false);

  const [addAssigneeForm, setAddAssigneeForm]     = useState({ open: false, name: '', email: '' });
  const [addAssigneeError, setAddAssigneeError]   = useState('');
  const [addAssigneeSaving, setAddAssigneeSaving] = useState(false);
  const [deletingAssignee, setDeletingAssignee]   = useState(null);

  // ── on login
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchFiles();
      fetchAssignees();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'users' && isAuthenticated) fetchUsers();
  }, [activeTab]);

  // ── auth ──────────────────────────────────────────────────────────────────
  // Single source of truth for the Authorization header — uses the JWT token.
  // Nothing else is sent; the raw password is never stored after login.
  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res  = await fetch(`${API_URL}/master/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUsername, password: loginPassword })
      });
      const data = await res.json();
      if (data.success && data.isAdmin && data.token) {
        setToken(data.token);           // store the JWT
        setLoggedInAs(loginUsername);
        setLoginPassword('');           // clear the password from state immediately
        setIsAuthenticated(true);
      } else {
        setLoginError('Access denied. Admin credentials required.');
      }
    } catch { setLoginError('Connection failed. Please try again.'); }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setToken(null);
    setLoggedInAs('');
    setFiles([]); setUsers([]); setAssignees([]);
    setLoginUsername(''); setLoginPassword('');
    setActiveTab('files');
  };

  // ── files ──────────────────────────────────────────────────────────────────
  const fetchFiles = async () => {
    setLoadingFiles(true); setFetchError('');
    try {
      const res = await fetch(`${API_URL}/master/files`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setFiles(await res.json());
    } catch { setFetchError('Failed to load files. Please refresh.'); }
    finally   { setLoadingFiles(false); }
  };

  const updateStatus = async (filename, status) => {
    setStatusUpdating(prev => ({ ...prev, [filename]: true }));
    setFiles(prev => prev.map(f => f.name === filename ? { ...f, status } : f));
    try {
      const res = await fetch(`${API_URL}/master/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ filename, status })
      });
      if (!res.ok) fetchFiles();
    } catch { fetchFiles(); }
    finally   { setStatusUpdating(prev => ({ ...prev, [filename]: false })); }
  };

  const updateAssign = async (filename, assigneeName) => {
    if (assigneeName === '__new__') {
      setInlineNew({ filename, name: '', email: '', saving: false, error: '' });
      return;
    }
    setAssignUpdating(prev => ({ ...prev, [filename]: true }));
    setFiles(prev => prev.map(f => f.name === filename ? { ...f, assignedTo: assigneeName } : f));
    try {
      const res = await fetch(`${API_URL}/master/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ filename, assigneeName })
      });
      if (!res.ok) fetchFiles();
    } catch { fetchFiles(); }
    finally   { setAssignUpdating(prev => ({ ...prev, [filename]: false })); }
  };

  const submitInlineNew = async (e) => {
    e.preventDefault();
    const { filename, name, email } = inlineNew;
    if (!name.trim()) return setInlineNew(p => ({ ...p, error: 'Name is required.' }));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setInlineNew(p => ({ ...p, error: 'Enter a valid email.' }));

    setInlineNew(p => ({ ...p, saving: true, error: '' }));
    try {
      const addRes  = await fetch(`${API_URL}/master/assignees/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ name: name.trim(), email: email.trim() })
      });
      const addData = await addRes.json();
      if (!addRes.ok) return setInlineNew(p => ({ ...p, saving: false, error: addData.error || 'Failed to add person.' }));
      setAssignees(addData.assignees);

      await fetch(`${API_URL}/master/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ filename, assigneeName: name.trim() })
      });
      setFiles(prev => prev.map(f => f.name === filename ? { ...f, assignedTo: name.trim() } : f));
      setInlineNew({ filename: null, name: '', email: '', saving: false, error: '' });
    } catch { setInlineNew(p => ({ ...p, saving: false, error: 'Request failed. Try again.' })); }
  };

  const toggleCollapse = company => setCollapsed(prev => ({ ...prev, [company]: !prev[company] }));

  const downloadFile = async (filename) => {
  if (downloadingFile === filename) return; // prevent double-click
  setDownloadingFile(filename);
  setDownloadPercent(0);
  try {
    const { ok, status } = await downloadWithProgress(
      `${API_URL}/master/download/${encodeURIComponent(filename)}`,
      filename,
      { headers: authHeader(), onProgress: setDownloadPercent }
    );

    if (!ok) {
      alert(`Download failed: ${status}`);
      return;
    }
  } catch (err) {
    console.error('Download error:', err);
    alert('Download failed. Please try again.');
  } finally {
    setDownloadingFile(null);
  }
};

  const deleteFile = async (filename) => {
    setFileDeleting(prev => ({ ...prev, [filename]: true }));
    try {
      const res = await fetch(`${API_URL}/master/file/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ filename })
      });
      if (res.ok) setFiles(prev => prev.filter(f => f.name !== filename));
    } catch (err) { console.error('Delete failed:', err); }
    finally {
      setFileDeleting(prev => ({ ...prev, [filename]: false }));
      setDeletingFile(null);
    }
  };

  // ── assignees ─────────────────────────────────────────────────────────────
  const fetchAssignees = async () => {
    try {
      const res = await fetch(`${API_URL}/master/assignees`, { headers: authHeader() });
      if (res.ok) setAssignees(await res.json());
    } catch { /* silent */ }
  };

  const handleAddAssignee = async (e) => {
    e.preventDefault();
    setAddAssigneeError('');
    const { name, email } = addAssigneeForm;
    if (!name.trim()) return setAddAssigneeError('Name is required.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setAddAssigneeError('Enter a valid email.');
    setAddAssigneeSaving(true);
    try {
      const res  = await fetch(`${API_URL}/master/assignees/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ name: name.trim(), email: email.trim() })
      });
      const data = await res.json();
      if (!res.ok) return setAddAssigneeError(data.error || 'Failed to add.');
      setAssignees(data.assignees);
      setAddAssigneeForm({ open: false, name: '', email: '' });
    } catch { setAddAssigneeError('Request failed. Try again.'); }
    finally   { setAddAssigneeSaving(false); }
  };

  const handleDeleteAssignee = async (email) => {
    try {
      const res  = await fetch(`${API_URL}/master/assignees/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) setAssignees(data.assignees);
    } catch { /* silent */ }
    finally   { setDeletingAssignee(null); }
  };

  // ── users ─────────────────────────────────────────────────────────────────
  const fetchUsers = async () => {
    setLoadingUsers(true); setUsersError('');
    try {
      const res = await fetch(`${API_URL}/master/users`, { headers: authHeader() });
      if (!res.ok) throw new Error();
      setUsers(await res.json());
    } catch { setUsersError('Failed to load users. Please refresh.'); }
    finally   { setLoadingUsers(false); }
  };

  const handleDeleteUser = async (targetUsername) => {
    try {
      const res = await fetch(`${API_URL}/master/users/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ targetUsername })
      });
      if (res.ok) setUsers(prev => prev.filter(u => u.username !== targetUsername));
    } catch { /* silent */ }
    finally   { setDeletingUser(null); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');
    const { username: nu, password: np, confirm: nc, isAdmin, isSupport, companies, email } = addUserForm;
    if (!nu.trim())    return setAddUserError('Username is required.');
    if (np.length < 8) return setAddUserError('Password must be at least 8 characters.');
    if (np !== nc)     return setAddUserError('Passwords do not match.');
    setAddUserSaving(true);
    try {
      const res  = await fetch(`${API_URL}/master/users/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          newUsername: nu.trim(),
          newPassword: np,
          isAdmin,
          isSupport,
          email: email.trim(),
          companies: companies.split(',').map(s => s.trim()).filter(Boolean)
        })
      });
      const data = await res.json();
      if (!res.ok) return setAddUserError(data.error || 'Failed to add user.');
      setAddUserForm({ open: false, username: '', password: '', confirm: '', isAdmin: false, isSupport: false, companies: '', email: '' });
      fetchUsers();
    } catch { setAddUserError('Request failed. Try again.'); }
    finally   { setAddUserSaving(false); }
  };

  // ── filter / sort helpers ─────────────────────────────────────────────────
  const grouped = files.reduce((acc, f) => {
    const c = f.companyName || 'Unknown';
    if (!acc[c]) acc[c] = [];
    acc[c].push(f);
    return acc;
  }, {});

  const getFileDate   = f  => f.uploadedAt || f.lastModified || f.createdOn || null;
  const obtainTail    = fn => { const i = (fn || '').indexOf('.'); return i === -1 ? fn || '—' : fn.slice(0, i); };
  const formatDate    = d  => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? String(d) : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const toggleSort    = key => setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const sortIndicator = key => sort.key !== key ? ' ⇅' : sort.dir === 'asc' ? ' ▲' : ' ▼';
  const setColFilter  = (col, val) => setColFilters(prev => ({ ...prev, [col]: val }));

  const applyFilters = (list) => {
    const gs = globalSearch.toLowerCase();
    return list.filter(f => {
      if (gs) {
        const hay = [f.name, f.serialNumber, formatDate(getFileDate(f)), obtainTail(f.name), f.status, f.assignedTo || ''].join(' ').toLowerCase();
        if (!hay.includes(gs)) return false;
      }
      if (colFilters.file   && !(f.name || '').toLowerCase().includes(colFilters.file.toLowerCase()))             return false;
      if (colFilters.serial && !(f.serialNumber || '').toLowerCase().includes(colFilters.serial.toLowerCase()))   return false;
      if (colFilters.date   && !formatDate(getFileDate(f)).toLowerCase().includes(colFilters.date.toLowerCase())) return false;
      if (colFilters.tail   && !obtainTail(f.name).toLowerCase().includes(colFilters.tail.toLowerCase()))         return false;
      if (colFilters.status && !(f.status || 'Pending').toLowerCase().includes(colFilters.status.toLowerCase()))  return false;
      if (colFilters.assign && !(f.assignedTo || '').toLowerCase().includes(colFilters.assign.toLowerCase()))     return false;
      return true;
    }).sort((a, b) => {
      if (!sort.key) return 0;
      let av, bv;
      if (sort.key === 'file')   { av = a.name || '';              bv = b.name || ''; }
      if (sort.key === 'serial') { av = a.serialNumber || '';      bv = b.serialNumber || ''; }
      if (sort.key === 'date')   { av = new Date(getFileDate(a) || 0).getTime(); bv = new Date(getFileDate(b) || 0).getTime(); }
      if (sort.key === 'tail')   { av = obtainTail(a.name);        bv = obtainTail(b.name); }
      if (sort.key === 'status') { av = a.status || 'Pending';     bv = b.status || 'Pending'; }
      if (sort.key === 'assign') { av = a.assignedTo || '';        bv = b.assignedTo || ''; }
      if (typeof av === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
      return sort.dir === 'asc'
        ? String(av).localeCompare(String(bv), undefined, { numeric: true })
        : String(bv).localeCompare(String(av), undefined, { numeric: true });
    });
  };

  const hasActiveFilters = globalSearch || Object.values(colFilters).some(Boolean);
  const clearFilters     = () => { setGlobalSearch(''); setColFilters({ file: '', serial: '', date: '', tail: '', status: '', assign: '' }); };
  const statusClass      = s => ({ Pending: 'status-pending', Processing: 'status-processing', Reviewed: 'status-reviewed', Complete: 'status-complete', Flagged: 'status-flagged' }[s] || '');

  // ── login screen ──────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="master-portal">
        <div className="master-login-wrapper">
          <img className="master-logo" src={logo} alt="Logo" />
          <h1>avCloudConnect</h1>
          <h2>Master Portal</h2>
          <form className="master-login-form" onSubmit={handleLogin}>
            <input type="text"     placeholder="Admin Username" value={loginUsername} onChange={e => setLoginUsername(e.target.value)} autoComplete="username" />
            <input type="password" placeholder="Password"       value={loginPassword} onChange={e => setLoginPassword(e.target.value)} autoComplete="current-password" />
            {loginError && <p className="master-error">{loginError}</p>}
            <button type="submit">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  // ── authenticated shell ───────────────────────────────────────────────────
  const companies = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  return (
    <div className="master-portal">

      {/* ── Header ── */}
      <div className="master-header">
        <img className="master-logo-small" src={logo} alt="Logo" />
        <div className="master-header-text">
          <h1>avCloudConnect</h1>
          <h2>Master Portal</h2>
        </div>
        <div className="master-tabs">
          {['files', 'users'].map(t => (
            <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div className="master-header-actions">
          {activeTab === 'files' && <button className="master-btn refresh-btn" onClick={fetchFiles} disabled={loadingFiles}>{loadingFiles ? 'Loading…' : 'Refresh'}</button>}
          {activeTab === 'users' && <button className="master-btn refresh-btn" onClick={fetchUsers} disabled={loadingUsers}>{loadingUsers ? 'Loading…' : 'Refresh'}</button>}
          <button className="master-btn logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* ── FILES TAB ──────────────────────────────────────────────────────── */}
      {activeTab === 'files' && (
        <div className="master-content">
          {/* Filter toolbar */}
          <div className="filter-toolbar">
            <div className="filter-toolbar-top">
              <input className="filter-global-search" type="text" placeholder="🔍 Search all columns…" value={globalSearch} onChange={e => setGlobalSearch(e.target.value)} />
              <button className={`master-btn filter-toggle-btn ${filtersVisible ? 'active' : ''}`} onClick={() => setFiltersVisible(v => !v)} type="button">
                {filtersVisible ? 'Hide Filters' : 'Column Filters'}
              </button>
              {hasActiveFilters && <button className="master-btn clear-filters-btn" onClick={clearFilters} type="button">✕ Clear</button>}
            </div>
            {filtersVisible && (
              <div className="filter-col-row">
                {[['file','File'],['serial','Serial #'],['date','Date'],['tail','Tail #'],['status','Status'],['assign','Assigned To']].map(([key, label]) => (
                  <div key={key} className="filter-col-item">
                    <label>{label}</label>
                    <input type="text" value={colFilters[key]} onChange={e => setColFilter(key, e.target.value)} placeholder="Filter…" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {fetchError && <div className="master-fetch-error">{fetchError}</div>}
          {loadingFiles && <div className="master-loading">Loading files…</div>}
          {!loadingFiles && companies.length === 0 && !fetchError && <div className="master-empty">No files found in storage.</div>}

          {companies.map(company => {
            const filtered    = applyFilters(grouped[company]);
            if (filtered.length === 0 && hasActiveFilters) return null;
            const rawCount    = grouped[company].length;
            const isCollapsed = !!collapsed[company];
            return (
              <div key={company} className="company-section">
                <button className="company-header" onClick={() => toggleCollapse(company)} type="button">
                  <span className="company-name">{company}</span>
                  <span className="company-count">
                    {hasActiveFilters ? `${filtered.length} / ${rawCount}` : rawCount} file{rawCount !== 1 ? 's' : ''}
                  </span>
                  <span className="collapse-icon">{isCollapsed ? '▶' : '▼'}</span>
                </button>

                {!isCollapsed && (
                  <div className="company-table-wrapper">
                    <table className="master-table">
                      <thead>
                        <tr>
                          <th onClick={() => toggleSort('file')}>File{sortIndicator('file')}</th>
                          <th onClick={() => toggleSort('serial')}>Serial{sortIndicator('serial')}</th>
                          <th onClick={() => toggleSort('date')}>Upload Date{sortIndicator('date')}</th>
                          <th onClick={() => toggleSort('tail')}>Tail{sortIndicator('tail')}</th>
                          <th onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                          <th onClick={() => toggleSort('assign')}>Assigned To{sortIndicator('assign')}</th>
                          <th>Actions</th>
                        </tr>
                        {filtersVisible && (
                          <tr className="filter-row">
                            <th><input value={colFilters.file}   onChange={e => setColFilter('file',   e.target.value)} placeholder="Filter…" /></th>
                            <th><input value={colFilters.serial} onChange={e => setColFilter('serial', e.target.value)} placeholder="Filter…" /></th>
                            <th><input value={colFilters.date}   onChange={e => setColFilter('date',   e.target.value)} placeholder="Filter…" /></th>
                            <th><input value={colFilters.tail}   onChange={e => setColFilter('tail',   e.target.value)} placeholder="Filter…" /></th>
                            <th><input value={colFilters.status} onChange={e => setColFilter('status', e.target.value)} placeholder="Filter…" /></th>
                            <th><input value={colFilters.assign} onChange={e => setColFilter('assign', e.target.value)} placeholder="Filter…" /></th>
                            <th></th>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {filtered.map(file => (
                          <React.Fragment key={file.name}>
                            <tr>
                              <td>{file.name}</td>
                              <td>{file.serialNumber || '—'}</td>
                              <td>{formatDate(getFileDate(file))}</td>
                              <td>{obtainTail(file.name)}</td>
                              <td>
                                <select
                                  className={`status-select ${statusClass(file.status || 'Pending')}`}
                                  value={file.status || 'Pending'}
                                  onChange={e => updateStatus(file.name, e.target.value)}
                                  disabled={!!statusUpdating[file.name]}
                                >
                                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </td>
                              <td>
                                <select
                                  className="assign-select"
                                  value={file.assignedTo || ''}
                                  onChange={e => updateAssign(file.name, e.target.value)}
                                  disabled={!!assignUpdating[file.name]}
                                >
                                  <option value="">Unassigned</option>
                                  {assignees.map(a => <option key={a.email} value={a.name}>{a.name}</option>)}
                                  <option value="__new__">+ Add new person…</option>
                                </select>
                              </td>
                              <td className="action-cell">
                                <button className="master-btn download-btn" onClick={() => downloadFile(file.name)} disabled={downloadingFile === file.name}>
                                  {downloadingFile === file.name ? 'Downloading…' : 'Download'}
                                </button>
                                {downloadingFile === file.name && (
                                  <div className="download-progress">
                                    <ProgressBar barHeight={6} progressPercentage={downloadPercent} />
                                  </div>
                                )}
                                {deletingFile === file.name ? (
                                  <span className="delete-confirm">
                                    <button className="master-btn confirm-delete-btn" onClick={() => deleteFile(file.name)} disabled={fileDeleting[file.name]}>
                                      {fileDeleting[file.name] ? 'Deleting…' : 'Confirm'}
                                    </button>
                                    <button className="master-btn cancel-btn" onClick={() => setDeletingFile(null)}>Cancel</button>
                                  </span>
                                ) : (
                                  <button className="master-btn delete-btn" onClick={() => setDeletingFile(file.name)}>Delete</button>
                                )}
                              </td>
                            </tr>
                            {inlineNew.filename === file.name && (
                              <tr className="inline-new-row">
                                <td colSpan={7}>
                                  <form className="inline-new-form" onSubmit={submitInlineNew}>
                                    <span className="inline-new-label">New person:</span>
                                    <input type="text"  placeholder="Full name" value={inlineNew.name}  onChange={e => setInlineNew(p => ({ ...p, name:  e.target.value }))} autoComplete="off" />
                                    <input type="email" placeholder="Email"     value={inlineNew.email} onChange={e => setInlineNew(p => ({ ...p, email: e.target.value }))} autoComplete="off" />
                                    {inlineNew.error && <span className="inline-new-error">{inlineNew.error}</span>}
                                    <button type="submit" className="um-btn save-btn"   disabled={inlineNew.saving}>{inlineNew.saving ? 'Saving…' : 'Save & Assign'}</button>
                                    <button type="button" className="um-btn cancel-btn" onClick={() => setInlineNew({ filename: null, name: '', email: '', saving: false, error: '' })}>Cancel</button>
                                  </form>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── USERS TAB ──────────────────────────────────────────────────────── */}
      {activeTab === 'users' && (
        <div className="master-content">
          {usersError && <div className="master-fetch-error">{usersError}</div>}
          {loadingUsers && <div className="master-loading">Loading users…</div>}

          {!loadingUsers && (
            <>
              {/* ── Login Users ── */}
              <div className="um-section">
                <div className="um-section-header">
                  <span>Login Users</span>
                  <button className="master-btn add-user-btn" onClick={() => { setAddUserForm(f => ({ ...f, open: !f.open })); setAddUserError(''); }}>
                    {addUserForm.open ? 'Cancel' : '+ Add User'}
                  </button>
                </div>

                {addUserForm.open && (
                  <form className="um-add-form" onSubmit={handleAddUser}>
                    <div className="um-add-row">
                      <input type="text"     placeholder="Username"          value={addUserForm.username} onChange={e => setAddUserForm(f => ({ ...f, username: e.target.value }))} autoComplete="off" />
                      <input type="password" placeholder="Password (min 8)" value={addUserForm.password} onChange={e => setAddUserForm(f => ({ ...f, password: e.target.value }))} autoComplete="new-password" />
                      <input type="password" placeholder="Confirm password" value={addUserForm.confirm}  onChange={e => setAddUserForm(f => ({ ...f, confirm:  e.target.value }))} autoComplete="new-password" />
                      <input type="email"    placeholder="Email (optional, for password reset)" value={addUserForm.email} onChange={e => setAddUserForm(f => ({ ...f, email: e.target.value }))} autoComplete="off" />
                    </div>
                    <div className="um-add-row um-add-role">
                      <label className="um-role-label">Role</label>
                      <select
                        className="um-role-select"
                        value={addUserForm.isAdmin ? 'admin' : addUserForm.isSupport ? 'support' : 'company'}
                        onChange={e => {
                          const role = e.target.value;
                          setAddUserForm(f => ({ ...f, isAdmin: role === 'admin', isSupport: role === 'support' }));
                        }}
                      >
                        <option value="company">Company user</option>
                        <option value="admin">Admin</option>
                        <option value="support">Support (read-only)</option>
                      </select>
                      {!addUserForm.isAdmin && !addUserForm.isSupport && (
                        <input type="text" placeholder="Companies (e.g. PSA,AFDS)" value={addUserForm.companies} onChange={e => setAddUserForm(f => ({ ...f, companies: e.target.value }))} className="um-companies-input" />
                      )}
                    </div>
                    {addUserError && <p className="um-error">{addUserError}</p>}
                    <button type="submit" className="master-btn save-btn" disabled={addUserSaving}>{addUserSaving ? 'Creating…' : 'Create User'}</button>
                  </form>
                )}

                <div className="um-user-list">
                  {users.map(user => (
                    <div key={user.username} className="um-user-row">
                      <div className="um-user-info">
                        <span className="um-username">{user.username}</span>
                        {user.isAdmin
                          ? <span className="um-role admin-badge">ADMIN</span>
                          : user.isSupport
                            ? <span className="um-role support-badge">SUPPORT</span>
                            : <span className="um-role company-badge">{user.companies.length ? user.companies.join(', ') : 'No companies'}</span>
                        }
                      </div>
                      <div className="um-user-actions">
                        <button className="um-btn change-pw-btn" onClick={() => setExpandedPw(expandedPw === user.username ? null : user.username)}>
                          {expandedPw === user.username ? 'Cancel' : 'Change Password'}
                        </button>
                        {deletingUser === user.username ? (
                          <div className="um-confirm">
                            <span>Delete {user.username}?</span>
                            <button className="um-btn confirm-delete-btn" onClick={() => handleDeleteUser(user.username)}>Yes, Delete</button>
                            <button className="um-btn cancel-btn" onClick={() => setDeletingUser(null)}>Cancel</button>
                          </div>
                        ) : (
                          loggedInAs.toLowerCase() !== user.username.toLowerCase() && (
                            <button className="um-btn delete-btn" onClick={() => setDeletingUser(user.username)}>Delete</button>
                          )
                        )}
                      </div>
                      {expandedPw === user.username && (
                        <div className="um-pw-expand">
                          <PasswordForm username={user.username} token={token} onDone={() => setExpandedPw(null)} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Assignees ── */}
              <div className="um-section">
                <div className="um-section-header">
                  <span>Assignees</span>
                  <button className="master-btn add-user-btn" onClick={() => { setAddAssigneeForm(f => ({ ...f, open: !f.open })); setAddAssigneeError(''); }}>
                    {addAssigneeForm.open ? 'Cancel' : '+ Add Assignee'}
                  </button>
                </div>
                <p className="um-section-desc">People who can be assigned files. They receive an email notification on assignment.</p>

                {addAssigneeForm.open && (
                  <form className="um-add-form" onSubmit={handleAddAssignee}>
                    <div className="um-add-row">
                      <input type="text"  placeholder="Full name" value={addAssigneeForm.name}  onChange={e => setAddAssigneeForm(f => ({ ...f, name:  e.target.value }))} autoComplete="off" />
                      <input type="email" placeholder="Email"     value={addAssigneeForm.email} onChange={e => setAddAssigneeForm(f => ({ ...f, email: e.target.value }))} autoComplete="off" />
                    </div>
                    {addAssigneeError && <p className="um-error">{addAssigneeError}</p>}
                    <button type="submit" className="master-btn save-btn" disabled={addAssigneeSaving}>{addAssigneeSaving ? 'Saving…' : 'Add Assignee'}</button>
                  </form>
                )}

                <div className="um-user-list">
                  {assignees.length === 0 && <p className="um-empty-msg">No assignees yet. Add one above.</p>}
                  {assignees.map(a => (
                    <div key={a.email} className="um-user-row">
                      <div className="um-user-info">
                        <span className="um-username">{a.name}</span>
                        <span className="um-role company-badge">{a.email}</span>
                      </div>
                      <div className="um-user-actions">
                        {deletingAssignee === a.email ? (
                          <div className="um-confirm">
                            <span>Remove {a.name}?</span>
                            <button className="um-btn confirm-delete-btn" onClick={() => handleDeleteAssignee(a.email)}>Yes, Remove</button>
                            <button className="um-btn cancel-btn" onClick={() => setDeletingAssignee(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button className="um-btn delete-btn" onClick={() => setDeletingAssignee(a.email)}>Remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MasterPortal;
